import {
    Directive,
    ElementRef,
    OnInit,
    Input,
    OnChanges,
    SimpleChanges,
    DoCheck,
    Renderer2,
    OnDestroy,
} from '@angular/core';
import { isDefined } from '../utils';

declare var autosize;

@Directive({
    selector: '[fiAutosize]',
    standalone: false,
})
export class AutosizeDirective implements OnInit, OnChanges, DoCheck, OnDestroy {
    element: HTMLElement;
    @Input() fiAutosize: string;
    @Input() ngModel;
    private isTextArea: boolean;
    private isVisible: boolean;
    private mirror: HTMLElement;
    private isAutosizeHeightOnly: boolean;
    private readonly mirrorStyleNamesArr: string[] = [
        'fontFamily',
        'fontSize',
        'fontWeight',
        'fontStyle',
        'lineHeight',
        'letterSpacing',
        'textTransform',
        'wordSpacing',
        'textIndent',
        'boxSizing',
        'borderRightWidth',
        'borderLeftWidth',
        'borderLeftStyle',
        'borderRightStyle',
        'paddingLeft',
        'paddingRight',
        'paddingTop',
        'paddingBottom',
        'marginLeft',
        'marginRight',
        'marginTop',
        'marginBottom',
    ];
    private elementEventListeners: Array<() => void> = [];
    constructor(
        private elementRef: ElementRef,
        private renderer: Renderer2,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.ngModel) {
            setTimeout(() => {
                if (this.isTextArea) {
                    autosize.update(this.element);
                } else {
                    this.update();
                }
            });
        }
    }

    ngDoCheck(): void {
        const newIsVisible = this.elementRef.nativeElement.offsetParent !== null;
        if (this.isVisible !== newIsVisible) {
            if (this.isTextArea) {
                autosize.update(this.element);
            } else {
                this.update();
            }
            this.isVisible = newIsVisible;
        }
    }

    ngOnInit() {
        this.element = this.elementRef.nativeElement as HTMLElement;
        this.isTextArea = this.element.tagName.toLowerCase() === 'textarea';
        if (this.isTextArea) {
            autosize(this.element);
        } else {
            const wrapper = this.getWrapperElement();
            this.mirror = this.getMirrorElement();
            const defaultMaxWidth = this.getDefaultMaxWidth();
            this.isAutosizeHeightOnly = this.isCalculateByHtml();
            if (!this.isAutosizeHeightOnly) {
                this.renderer.setStyle(
                    this.element,
                    'minWidth',
                    this.element.getAttribute('puElasticInputMinwidth') || this.element.style.minWidth,
                );
                this.renderer.setStyle(
                    this.element,
                    'maxWidth',
                    this.element.getAttribute('puElasticInputMaxwidth') || defaultMaxWidth,
                );
            }
            this.mirrorStyleNamesArr.forEach((value) => {
                this.renderer.setStyle(this.mirror, value, this.element.style[value]);
            });
            wrapper.appendChild(this.mirror);
            document.body.appendChild(wrapper);
            this.update();

            if (!this.ngModel) {
                this.elementEventListeners.push(this.renderer.listen(this.element, 'keydown', () => this.update()));
                this.elementEventListeners.push(this.renderer.listen(this.element, 'keyup', () => this.update()));
                this.elementEventListeners.push(this.renderer.listen(this.element, 'focus', () => this.update()));
                this.elementEventListeners.push(this.renderer.listen(this.element, 'input', () => this.update()));
                this.elementEventListeners.push(this.renderer.listen(this.element, 'change', () => this.update()));
                this.elementEventListeners.push(
                    this.renderer.listen(this.element, 'propertychange', () => this.update()),
                );
            }
        }
    }

    update() {
        if (!this.isCalculateByHtml()) {
            this.mirror.innerText = this.element.nodeValue || this.element.getAttribute('placeholder') || '';
        }

        if (this.isAutosizeHeightOnly) {
            if (!this.isCalculateByHtml()) {
                this.mirror.innerText += '\n';
                const height = this.mirror.offsetHeight + 6;
                this.renderer.setStyle(this.element, 'height', height + 'px');
            } else {
                this.renderer.setStyle(this.element, 'height', 'auto');
            }
        } else {
            this.renderer.setStyle(this.element, 'width', this.mirror.offsetWidth + 1.04 + 1 + 'px');
        }
    }
    private isCalculateByHtml(): boolean {
        return isDefined(this.fiAutosize) && this.fiAutosize.indexOf('html') >= 0;
    }

    private getWrapperElement(): HTMLElement {
        const wrapper = document.createElement('div');
        this.renderer.setAttribute(wrapper, 'style', 'position:fixed; top:-999px; left:0;');
        return wrapper;
    }

    private getMirrorElement(): HTMLElement {
        const span = document.createElement('span');
        this.renderer.setStyle(span, 'whiteSpace', 'pre');
        return span;
    }

    private getDefaultMaxWidth(): string | number {
        return this.element.style.maxWidth === 'none'
            ? this.element.parentElement.clientWidth
            : this.element.style.maxWidth;
    }

    ngOnDestroy() {
        this.elementEventListeners.forEach((listener) => listener());
        this.elementEventListeners = [];
    }
}
